/**
 * PlayCanvas Configurator Bridge
 *
 * Maps UI attribute names (Threekit-style) → PlayCanvas configurator keys/values.
 * Uses window.configurator.setConfig() / getConfig() / getAvailableOptions()
 */

import { getLocalIcon } from '../assets/icon-props';
import productsJson from '../../products.json';
import { getMockAttributes } from './threekitMocks';

// ── Vivid image URL normalization ──────────────────────────────
// DigitalOcean Spaces origin URLs → CDN URLs for better performance.
const DO_ORIGIN_PATTERN = /\.fra1\.digitaloceanspaces\.com/;
const DO_CDN_REPLACEMENT = '.fra1.cdn.digitaloceanspaces.com';

/**
 * Normalizes a Vivid / DigitalOcean Spaces image URL to use the CDN endpoint.
 * e.g. `https://2d-render-admin-storage.fra1.digitaloceanspaces.com/…`
 *    → `https://2d-render-admin-storage.fra1.cdn.digitaloceanspaces.com/…`
 *
 * Returns the original value unchanged when it's falsy or already a CDN URL.
 */
export function normalizeVividImageUrl(url: string | null | undefined): string | null | undefined {
  if (!url) return url;
  // Already CDN — nothing to do
  if (url.includes('.fra1.cdn.digitaloceanspaces.com')) return url;
  // Origin → CDN
  if (DO_ORIGIN_PATTERN.test(url)) {
    return url.replace(DO_ORIGIN_PATTERN, DO_CDN_REPLACEMENT);
  }
  return url;
}

// ── PlayCanvas configurator type on window ──
export interface PlayCanvasConfigurator {
  setConfig: (config: Partial<PlayCanvasConfig>) => void | Promise<void>;
  getConfig: () => PlayCanvasConfig;
  getAvailableOptions: () => Record<string, PlayCanvasOption>;
  subscribe?: (
    cb: (newCfg: PlayCanvasConfig, oldCfg: PlayCanvasConfig) => void
  ) => void;
}

export interface PlayCanvasConfig {
  jeepType: string;
  rimOption: string;
  tireOption: string;
  frontBumper: string;
  rearBumper: string;
  frontFender: string;
  rearFender: string;
  mirrorOption: string;
  hoodCatcherOption: string;
  lightBarOption: string;
  sideStepOption: string;
  exteriorColorId: number;
  interiorColorId: number;
  [key: string]: string | number | boolean;
}

export interface PlayCanvasOption {
  type: string;
  options: Array<{ value: string | number; label: string }>;
}

// ── Mapping: UI attribute name → PlayCanvas config key ──
export const ATTR_TO_PC_KEY: Record<string, string> = {
  'Jeep Type': 'jeepType',
  'Paint color': 'exteriorColorId',
  Wheels: 'rimOption',
  Tire: 'tireOption',
  'Front Bumper': 'frontBumper',
  'Rear Bumper': 'rearBumper',
  'Front Fender': 'frontFender',
  'Rear Fender': 'rearFender',
  Mirror: 'mirrorOption',
  'Hood Catcher': 'hoodCatcherOption',
  'Light Bar': 'lightBarOption',
  'Side Steps': 'sideStepOption',
  Door: 'doorOption',
};

// ── Reverse mapping: PlayCanvas key → UI attribute name ──
export const PC_KEY_TO_ATTR: Record<string, string> = Object.fromEntries(
  Object.entries(ATTR_TO_PC_KEY).map(([k, v]) => [v, k])
);

// ── Color label → exteriorColorId mapping ──
const COLOR_TO_ID: Record<string, number> = {
  Red: 0,
  Black: 1,
  White: 2,
  Yellow: 3,
  Green: 4,
  Grey: 5,
};

const ID_TO_COLOR: Record<number, string> = Object.fromEntries(
  Object.entries(COLOR_TO_ID).map(([k, v]) => [v, k])
);

// ── Route → jeepType ──
export const ROUTE_TO_JEEP_TYPE: Record<string, string> = {
  '/Jeep2DoorJL': '2d',
  '/Jeep4DoorJL': '4d',
  Jeep2DoorJL: '2d',
  Jeep4DoorJL: '4d',
};

// ── Value transformers ──

/**
 * Converts a UI attribute value to a PlayCanvas config value.
 *
 * For most attributes: the metadata.option (or .value) is already the PlayCanvas value.
 * For Paint color:  UI assetId (e.g. 'green-paint') → label → exteriorColorId (number).
 */
export function uiValueToPC(
  attrName: string,
  uiValue: any,
  label?: string
): any {
  const pcKey = ATTR_TO_PC_KEY[attrName];
  if (!pcKey) return undefined;

  if (pcKey === 'exteriorColorId') {
    // Resolve by label → color index
    if (label && label in COLOR_TO_ID) {
      return COLOR_TO_ID[label];
    }
    return undefined;
  }

  // For string-type attributes: uiValue is the PlayCanvas option string
  // (e.g. 'whl-quad-hardroc', 'carnivore', '4d')
  return uiValue;
}

/**
 * Converts a PlayCanvas config value back to the UI attribute value.
 */
export function pcValueToUI(pcKey: string, pcValue: any): any {
  if (pcKey === 'exteriorColorId' && typeof pcValue === 'number') {
    return ID_TO_COLOR[pcValue] ?? 'Black';
  }
  return pcValue;
}

// ── Helper: get configurator from window (if loaded) ──
export function getPlayCanvasConfigurator(): PlayCanvasConfigurator | null {
  return (window as any).configurator ?? null;
}

/**
 * Set a single UI attribute on the PlayCanvas configurator.
 * Safe to call even when the configurator hasn't loaded yet (no-op).
 */
export function setPlayCanvasAttribute(
  attrName: string,
  uiValue: any,
  label?: string
): void {
  const configurator = getPlayCanvasConfigurator();
  if (!configurator?.setConfig) return;

  const pcKey = ATTR_TO_PC_KEY[attrName];
  if (!pcKey) return;

  const pcValue = uiValueToPC(attrName, uiValue, label);
  if (pcValue === undefined) return;

  configurator.setConfig({ [pcKey]: pcValue });
}

/**
 * Set multiple attributes at once on the PlayCanvas configurator.
 */
export function setPlayCanvasBatch(
  entries: Array<{ attrName: string; uiValue: any; label?: string }>
): void {
  const configurator = getPlayCanvasConfigurator();
  if (!configurator?.setConfig) return;

  const patch: Record<string, any> = {};
  for (const { attrName, uiValue, label } of entries) {
    const pcKey = ATTR_TO_PC_KEY[attrName];
    if (!pcKey) continue;
    const pcValue = uiValueToPC(attrName, uiValue, label);
    if (pcValue !== undefined) {
      patch[pcKey] = pcValue;
    }
  }

  if (Object.keys(patch).length > 0) {
    configurator.setConfig(patch);
  }
}

// ── Active-item data shape passed to the callback ──
export interface ActiveItemData {
  activeItem: string;
  activeItemId?: string;
  img: string;
  price: number;
}

/**
 * Reads window.configurator.getConfig() + getAvailableOptions() and resolves,
 * for every mapped attribute, the label, icon, and price from the current config.
 *
 * Calls onAttribute(attrName, data) for each resolved attribute so the caller
 * (e.g. PlayCanvasPlayer) can dispatch SetActiveItem without creating a Redux
 * dependency inside this utility module.
 */
export function syncStoreFromPlayCanvasConfig(
  onAttribute: (attrName: string, data: ActiveItemData) => void
): void {
  const configurator = getPlayCanvasConfigurator();
  if (!configurator?.getConfig) return;

  const cfg = configurator.getConfig();

  let opts: Record<string, PlayCanvasOption> = {};
  try {
    opts = configurator.getAvailableOptions?.() ?? {};
  } catch {
    opts = {};
  }

  Object.entries(cfg).forEach(([pcKey, pcValue]) => {
    const attrName = PC_KEY_TO_ATTR[pcKey];
    if (!attrName) return;

    let label: string;
    let optionValue: string | undefined;

    if (pcKey === 'exteriorColorId') {
      // Numeric color id → human label
      label = pcValueToUI(pcKey, pcValue) as string;
      optionValue = undefined;
    } else {
      // Find the matching option entry from getAvailableOptions()
      const attributeOpts = opts[pcKey];
      const match = attributeOpts?.options.find((o) => o.value === pcValue);
      label = match?.label ?? String(pcValue);
      // Prefer the matched value (string), fall back to the raw PC value
      optionValue = match ? String(match.value) : String(pcValue);
    }

    // Price: look up by optionValue in products.json (covers IDs like 'hc-def', etc.)
    const productEntry = optionValue
      ? (productsJson as Record<string, any>)[optionValue]
      : undefined;
    const price: number = productEntry?.price ?? 0;

    // Icon: prefer API image from mock metadata, then local icon fallback
    const jeepType = (cfg.jeepType === '2d' ? '2d' : '4d') as '2d' | '4d';
    const mockAttr = getMockAttributes()[attrName];
    const matchedVariant = mockAttr?.values?.find((v: any) => v.label === label || v.value === optionValue);
    const apiImage = normalizeVividImageUrl(matchedVariant?.metadata?._img) || undefined;
    const img = apiImage || getLocalIcon(optionValue, label, attrName, jeepType) || '';

    onAttribute(attrName, { activeItem: label, activeItemId: optionValue, img, price });
  });
}

/**
 * Reset the PlayCanvas configurator to default values.
 * Only resets keys that actually exist in the current PlayCanvas config
 * so we never send unknown keys that could cause issues.
 */
export function resetPlayCanvasToDefaults(): void {
  const configurator = getPlayCanvasConfigurator();
  if (!configurator?.setConfig) return;

  // 1. Read what keys PlayCanvas currently has
  let currentConfig: Record<string, any> = {};
  try {
    currentConfig = configurator.getConfig?.() ?? {};
  } catch {
    return; // configurator not ready
  }

  const existingPcKeys = new Set(Object.keys(currentConfig));

  // 2. Build a patch with only keys that exist in PlayCanvas
  const mocks = getMockAttributes();
  const patch: Record<string, any> = {};

  for (const [attrName, attr] of Object.entries(mocks) as [string, any][]) {
    const pcKey = ATTR_TO_PC_KEY[attrName];
    if (!pcKey || !attr) continue;

    // Skip keys that PlayCanvas doesn't have
    if (!existingPcKeys.has(pcKey)) continue;

    // Find the default variant
    const isAsset = attr.type === 'Asset';
    const defaultId = isAsset ? attr.defaultValue?.assetId : attr.defaultValue;
    const defaultVariant = attr.values?.find((v: any) =>
      isAsset ? v.assetId === defaultId : v.value === defaultId
    );

    if (!defaultVariant) continue;

    const uiValue = defaultVariant.value ?? defaultVariant.metadata?.option;
    const label = defaultVariant.label;
    const pcValue = uiValueToPC(attrName, uiValue, label);

    if (pcValue !== undefined) {
      patch[pcKey] = pcValue;
    }
  }

  if (Object.keys(patch).length > 0) {
    console.log('[playcanvasConfig] Resetting PlayCanvas to defaults:', patch);
    console.log('[playcanvasConfig] PlayCanvas existing keys:', Array.from(existingPcKeys));
    configurator.setConfig(patch);
  }
}
